import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_AVAILABILITY, type StaffMember, type WeeklySchedule } from '@acs/shared';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Icon,
  Input,
  PageHeader,
  SaveBar,
} from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { PageLoader } from '../../shared/ui/PageLoader.js';
import { friendlyError } from '../../shared/api/errors.js';
import {
  weekdayNames,
  TimeGrid,
  WeeklyScheduleEditor,
  visibleWindow,
  weeklyToDays,
  type TimeGridBlock,
  type TimeGridColumn,
} from '../schedule-grid/index.js';
import { DateOverridesEditor } from './DateOverridesEditor.js';
import {
  useBusinessAvailability,
  useDeleteStaffMember,
  useServicesForStaff,
  useStaff,
  useUpsertStaffMember,
} from './hooks.js';
import { useI18n } from '../../i18n/index.js';

type Tab = 'weekly' | 'overrides';

export function StaffEditorPage() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  const { data: staff, isLoading } = useStaff(business.id);
  const { data: services } = useServicesForStaff(business.id);
  const { data: availabilityDoc } = useBusinessAvailability(business.id);
  const availability = availabilityDoc ?? DEFAULT_AVAILABILITY;
  const upsert = useUpsertStaffMember(business.id);

  const member = staff?.find((s) => s.id === staffId);

  const [draft, setDraft] = useState<StaffMember | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const [tab, setTab] = useState<Tab>('weekly');

  useEffect(() => {
    if (member && draft === null) {
      const { id: _id, ...rest } = member;
      setDraft(rest);
      setSnapshot(JSON.stringify(rest));
    }
  }, [member, draft]);

  const dirty = draft !== null && JSON.stringify(draft) !== snapshot;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (isLoading || (staff && member && draft === null)) {
    return <PageLoader />;
  }
  if (staff && !member) return <Navigate to={`/b/${business.id}/staff`} replace />;
  if (!draft) return <PageLoader />;

  const patch = (p: Partial<StaffMember>) => setDraft({ ...draft, ...p });
  const customized = draft.schedule !== null;

  const onSave = () =>
    upsert.mutate(
      { staff: draft, id: staffId },
      { onSuccess: () => setSnapshot(JSON.stringify(draft)) },
    );

  return (
    <>
      <PageHeader
        back={
          <Button
            variant="link"
            size="sm"
            leadingIcon={<Icon name="arrow-left" size={16} />}
            onClick={() => navigate(`/b/${business.id}/staff`)}
          >
            {t('staff.allStaff')}
          </Button>
        }
        title={draft.name}
        subtitle={t('staff.editor.subtitle')}
      />

      <div className="grid">
        <DetailsCard draft={draft} services={services ?? []} onPatch={patch} />

        <Card
          title={t('staff.editor.schedule')}
          actions={
            <Badge tone={customized ? 'info' : 'neutral'}>
              {t(customized ? 'staff.customSchedule' : 'staff.businessHours')}
            </Badge>
          }
        >
          <div className="segmented" role="tablist" aria-label={t('staff.editor.scheduleTab')} style={{ marginBottom: 16 }}>
            <button type="button" role="tab" aria-selected={tab === 'weekly'} className={tab === 'weekly' ? 'is-active' : ''} onClick={() => setTab('weekly')}>
              {t('staff.editor.weekly')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'overrides'}
              className={tab === 'overrides' ? 'is-active' : ''}
              disabled={!customized}
              title={customized ? undefined : t('staff.editor.overridesDisabled')}
              onClick={() => customized && setTab('overrides')}
            >
              {t('staff.editor.overrides')}
            </button>
          </div>

          <InheritBanner
            customized={customized}
            onCustomize={(fromBusiness) =>
              patch({ schedule: { hours: fromBusiness ? { ...availability.hours } : {}, exceptions: {} } })
            }
            onReset={() => {
              patch({ schedule: null });
              setTab('weekly');
            }}
          />

          {tab === 'weekly' ? (
            customized ? (
              <WeeklyScheduleEditor
                value={draft.schedule!.hours}
                onChange={(hours) => patch({ schedule: { ...draft.schedule!, hours } })}
              />
            ) : (
              <GhostGrid hours={availability.hours} />
            )
          ) : (
            <DateOverridesEditor
              schedule={draft.schedule!}
              business={availability}
              onChange={(schedule) => patch({ schedule })}
            />
          )}
        </Card>

        <DeleteCard
          name={member?.name ?? draft.name}
          onDeleted={() => navigate(`/b/${business.id}/staff`, { replace: true })}
          staffId={staffId!}
          businessId={business.id}
        />
      </div>

      {dirty && (
        <SaveBar
          regionLabel={t('ui.unsavedChanges')}
          message={t('staff.editor.unsaved', { name: draft.name })}
          saveLabel={t('staff.editor.saveSchedule')}
          discardLabel={t('common.discard')}
          saving={upsert.isPending}
          onDiscard={() => setDraft(JSON.parse(snapshot))}
          onSave={onSave}
        />
      )}
    </>
  );
}

function DetailsCard({
  draft,
  services,
  onPatch,
}: {
  draft: StaffMember;
  services: { id: string; name: string; active: boolean }[];
  onPatch(p: Partial<StaffMember>): void;
}) {
  const { t } = useI18n();
  const toggleService = (id: string) => {
    const has = draft.serviceIds.includes(id);
    onPatch({ serviceIds: has ? draft.serviceIds.filter((s) => s !== id) : [...draft.serviceIds, id] });
  };
  return (
    <Card title={t('staff.editor.details')}>
      <Field label={t('clients.name')} hint={t('staff.editor.nameHint')}>
        <Input value={draft.name} onChange={(e) => onPatch({ name: e.target.value })} maxLength={80} />
      </Field>
      <Checkbox
        checked={draft.active}
        onChange={(e) => onPatch({ active: e.target.checked })}
        label={t('staff.editor.activeLabel')}
      />
      <div className="field" style={{ marginTop: 16 }}>
        <span className="field-label">{t('staff.editor.services')}</span>
        <span className="field-hint">{t('staff.editor.servicesHint')}</span>
        <div className="staff-services">
          {services.filter((s) => s.active).map((s) => (
            <Checkbox
              key={s.id}
              checked={draft.serviceIds.includes(s.id)}
              onChange={() => toggleService(s.id)}
              label={s.name}
            />
          ))}
          {services.filter((s) => s.active).length === 0 && (
            <span className="muted">{t('staff.editor.noServices')}</span>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Deleting lives here, behind a confirmation — never one tap away on the list. */
function DeleteCard({
  businessId,
  staffId,
  name,
  onDeleted,
}: {
  businessId: string;
  staffId: string;
  /** The SAVED name: a half-typed rename is not who is being deleted. */
  name: string;
  onDeleted(): void;
}) {
  const { t } = useI18n();
  const del = useDeleteStaffMember(businessId);
  const [confirming, setConfirming] = useState(false);
  return (
    <Card title={t('staff.editor.delete.title')}>
      <p className="field-hint" style={{ marginTop: 0 }}>{t('staff.editor.delete.hint')}</p>
      {del.isError && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="danger">{friendlyError(t, del.error)}</Banner>
        </div>
      )}
      <Button variant="danger" leadingIcon={<Icon name="trash" size={16} />} onClick={() => setConfirming(true)}>
        {t('staff.deleteLabel', { name })}
      </Button>
      {confirming && (
        <ConfirmDialog
          title={t('staff.delete.title', { name })}
          description={t('staff.delete.description')}
          icon={<Icon name="trash" size={24} />}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          loading={del.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            del.mutate(staffId, { onSuccess: onDeleted, onError: () => setConfirming(false) })
          }
        />
      )}
    </Card>
  );
}

function InheritBanner({
  customized,
  onCustomize,
  onReset,
}: {
  customized: boolean;
  onCustomize(fromBusiness: boolean): void;
  onReset(): void;
}) {
  const { t } = useI18n();
  const [choosing, setChoosing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  if (customized) {
    return (
      <>
        <div className="inherit-banner">
          <span>{t('staff.editor.customBanner')}</span>
          <Button size="sm" variant="secondary" onClick={() => setConfirmReset(true)}>
            {t('staff.editor.reset')}
          </Button>
        </div>
        {confirmReset && (
          <ConfirmDialog
            tone="warning"
            title={t('staff.editor.reset.title')}
            description={t('staff.editor.reset.description')}
            icon={<Icon name="refresh" size={24} />}
            confirmLabel={t('staff.editor.reset.confirm')}
            cancelLabel={t('common.cancel')}
            onCancel={() => setConfirmReset(false)}
            onConfirm={() => {
              setConfirmReset(false);
              onReset();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="inherit-banner">
      <span>{t('staff.editor.inheritBanner')}</span>
      {choosing ? (
        <span className="row" style={{ gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => onCustomize(true)}>
            {t('staff.editor.startFromBusiness')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onCustomize(false)}>
            {t('staff.editor.startEmpty')}
          </Button>
        </span>
      ) : (
        <Button size="sm" onClick={() => setChoosing(true)}>{t('staff.editor.customize')}</Button>
      )}
    </div>
  );
}

/** Read-only preview of the business hours a null-schedule staff member inherits. */
function GhostGrid({ hours }: { hours: WeeklySchedule['hours'] }) {
  const { t, tag } = useI18n();
  const days = weeklyToDays(hours);
  const columns: TimeGridColumn[] = weekdayNames(tag, 'short').map((label, i) => ({ id: String(i), label }));
  const blocks: TimeGridBlock[] = days.flatMap((intervals, i) =>
    intervals.map((iv) => ({ id: `g${i}#${iv.startMin}`, columnId: String(i), kind: 'ghost' as const, ...iv })),
  );
  return (
    <div>
      <TimeGrid columns={columns} blocks={blocks} window={visibleWindow(days)} onCommit={() => {}} readOnly />
      <p className="field-hint" style={{ marginTop: 8 }}>{t('staff.editor.shadedHint')}</p>
    </div>
  );
}
